import { Link } from 'react-router-dom';

const coreServices = [
  {
    id: 'custom-suits',
    title: 'Custom Suits',
    badge: 'Most Popular',
    badgeColor: 'bg-[#C4A962]',
    description:
      'Our hallmark service. Every suit is built from scratch to your exact specifications — fabric, lining, silhouette, lapels, pockets, and every detail. No shortcuts, no compromises, no standard sizing.',
    bullets: [
      '30+ custom body measurements for a flawless fit',
      '5,000+ premium fabrics — Italian wools, cashmere, linens',
      'Fully bespoke construction — 100% custom made',
      'Custom lining, monogram, and pocket styling',
      'Multiple fittings until every detail is perfect',
    ],
    image:
      'https://public.readdy.ai/ai/img_res/5f94458cebf998d0b80690a9e795612b.jpg',
    link: '/custom-suits',
    cta: 'Explore Custom Suits',
    icon: 'ri-user-3-line',
  },
  {
    id: 'custom-shirts',
    title: 'Custom Dress Shirts',
    badge: null,
    badgeColor: '',
    description:
      'A perfectly fitted dress shirt is the foundation of every great outfit. Choose your fabric, collar, cuffs, placket, and more — all measured and cut precisely to your unique body.',
    bullets: [
      '20 different collar styles to suit your preference',
      'French, single, and double cuff options',
      'Premium Egyptian cotton, fine broadcloth, and poplin',
      'Sleeve length and body taper to exact measurements',
      '100% custom — no off-the-rack compromises',
    ],
    image:
      'https://readdy.ai/api/search-image?query=pristine%20white%20bespoke%20dress%20shirt%20with%20semi-spread%20collar%20mother%20of%20pearl%20buttons%20and%20perfectly%20pressed%20pleats%20hanging%20on%20polished%20brass%20hanger%20against%20warm%20amber%20backlit%20wooden%20paneling%20in%20luxury%20tailoring%20studio%20fine%20Egyptian%20cotton%20texture%20visible%20subtle%20light%20sheen%20premium%20craftsmanship%20editorial%20photography%20Massachusetts&width=900&height=1100&seq=shirt-editorial-0312&orientation=portrait',
    link: '/custom-shirts',
    cta: 'Explore Custom Shirts',
    icon: 'ri-shirt-line',
  },
  {
    id: 'wedding-formalwear',
    title: 'Wedding & Formalwear',
    badge: 'Special Occasion',
    badgeColor: 'bg-gray-900',
    description:
      'Your most important day deserves the most important suit. We specialize in bespoke wedding suits, tuxedos, and formalwear for grooms, groomsmen, and every black-tie occasion.',
    bullets: [
      'Fully bespoke wedding suits and tuxedos from scratch',
      'Groomsmen packages — coordinated and cohesive',
      'Black-tie, white-tie, and cocktail attire',
      'Expert style consultation for every occasion',
      'Tailored to coordinate across your entire wedding party',
    ],
    image:
      'https://readdy.ai/api/search-image?query=elegantly%20dressed%20groom%20wearing%20a%20flawlessly%20fitted%20ivory%20white%20peak%20lapel%20wedding%20suit%20with%20silk%20pocket%20square%20and%20boutonniere%20standing%20in%20sun-drenched%20garden%20ceremony%20venue%20with%20soft%20bokeh%20green%20foliage%20background%20romantic%20warm%20golden%20light%20bespoke%20custom%20clothier%20Massachusetts%20formalwear%20editorial%20photography&width=900&height=1100&seq=wedding-editorial-0313&orientation=portrait',
    link: '/contact',
    cta: 'Plan Your Wedding Look',
    icon: 'ri-vip-crown-line',
  },
  {
    id: 'alterations',
    title: 'Alterations & Tailoring',
    badge: null,
    badgeColor: '',
    description:
      'We breathe new life into garments you already own. From simple hemming to full suit restructuring, our master tailors deliver precision alterations with a bespoke-level standard.',
    bullets: [
      'Suit and jacket alterations — sleeves, body, shoulders',
      'Trouser alterations — waist, inseam, taper, hem',
      'Dress shirt alterations and resizing',
      'Zipper, lining, and button repairs and replacements',
      'Full suit modernization and style transformation',
    ],
    image:
      'https://readdy.ai/api/search-image?query=skilled%20master%20tailor%20with%20silver%20thimble%20on%20finger%20carefully%20pinning%20and%20chalk%20marking%20the%20sleeve%20of%20a%20dark%20navy%20wool%20suit%20jacket%20on%20a%20professional%20dress%20form%20in%20warm%20workshop%20light%20with%20measuring%20tape%20draped%20around%20neck%20tailoring%20tools%20scissors%20chalk%20on%20dark%20wooden%20work%20table%20craftsmanship%20detail%20editorial%20photo%20Massachusetts%20bespoke%20clothier&width=900&height=1100&seq=alt-editorial-0314&orientation=portrait',
    link: '/alterations',
    cta: 'Get Expert Alterations',
    icon: 'ri-scissors-2-line',
  },
];

const CoreServicesSection = () => {
  return (
    <section className="py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-24">
          <p className="text-[#C4A962] font-semibold tracking-widest uppercase text-sm mb-4">
            What We Offer
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-5">
            Our Core Services
          </h2>
          <p className="text-xl text-gray-500 max-w-2xl mx-auto">
            Every service is personal, precise, and built on over 55 years of master craftsmanship.
          </p>
        </div>

        <div className="space-y-32">
          {coreServices.map((service, index) => (
            <div
              key={service.id}
              className="grid grid-cols-1 lg:grid-cols-2 gap-0 items-stretch rounded-3xl overflow-hidden border border-gray-100"
            >
              {/* Image */}
              <div
                className={`relative ${index % 2 === 1 ? 'lg:order-2' : 'lg:order-1'}`}
              >
                <div className="h-[600px] lg:h-full min-h-[500px]">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                </div>
                {service.badge && (
                  <div className={`absolute top-6 left-6 ${service.badgeColor} text-white text-xs font-bold px-4 py-2 rounded-full tracking-wide`}>
                    {service.badge}
                  </div>
                )}
              </div>

              {/* Content */}
              <div className={`${index % 2 === 1 ? 'lg:order-1' : 'lg:order-2'} bg-[#FAFAF8] p-12 lg:p-14 flex flex-col justify-center`}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-14 h-14 bg-[#C4A962]/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <i className={`${service.icon} text-[#C4A962] text-2xl`}></i>
                  </div>
                  <h3 className="text-3xl md:text-4xl font-bold text-gray-900">{service.title}</h3>
                </div>
                <p className="text-gray-500 text-lg leading-relaxed mb-8">{service.description}</p>
                <ul className="space-y-4 mb-10">
                  {service.bullets.map((bullet, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-[#C4A962] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-white text-xs"></i>
                      </div>
                      <span className="text-gray-700 leading-relaxed">{bullet}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to={service.link}
                    className="inline-flex items-center justify-center bg-[#C4A962] text-white px-8 py-4 rounded-xl font-semibold hover:bg-[#B39952] transition-colors whitespace-nowrap text-base"
                  >
                    {service.cta}
                    <i className="ri-arrow-right-line ml-2"></i>
                  </Link>
                  <a
                    href="tel:781-237-3166"
                    className="inline-flex items-center justify-center border border-gray-300 text-gray-700 px-8 py-4 rounded-xl font-semibold hover:border-[#C4A962] hover:text-[#C4A962] transition-colors whitespace-nowrap text-base"
                  >
                    <i className="ri-phone-line mr-2"></i>Call Us
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CoreServicesSection;
